import { useEffect, useState, useCallback } from "react";
import { Sparkles, Loader2, Plus, Trash2, Star, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { author: "", rating: 5, text: "", location: "Costa Rica", pet_type: "perro", approved: true };

export const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(4);
  const [generating, setGenerating] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/reviews"); setReviews(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/reviews/generate", { count: Number(count) });
      toast.success(`Se generaron ${data.count} reseñas`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo generar");
    } finally { setGenerating(false); }
  };

  const save = async () => {
    if (!form.author.trim() || !form.text.trim()) { toast.error("Completa autor y texto"); return; }
    setSaving(true);
    try {
      await api.post("/reviews", { ...form, rating: Number(form.rating) });
      toast.success("Reseña agregada"); setOpen(false); setForm(EMPTY); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm("¿Eliminar esta reseña?")) return;
    await api.delete(`/reviews/${r.id}`); toast.success("Eliminada"); load();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-[#E3E7E2] p-5">
        <h3 className="font-display text-lg font-bold text-charcoal mb-1">Generar reseñas con IA</h3>
        <p className="text-sm text-muted-moss mb-3">Crea reseñas realistas para mostrar en la página principal.</p>
        <div className="flex items-center gap-2">
          <select value={count} onChange={(e) => setCount(e.target.value)} className="inp w-24">
            {[2, 3, 4, 5, 6, 8].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button data-testid="reviews-generate-button" onClick={generate} disabled={generating} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-coral text-white font-semibold hover:bg-coral-dark active:scale-95 transition-all disabled:opacity-60">
            {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />} Generar
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-moss">{reviews.length} reseña(s)</p>
        <button onClick={() => { setForm(EMPTY); setOpen(true); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white border border-[#E3E7E2] text-sm font-semibold text-charcoal hover:bg-sage/40"><Plus className="w-4 h-4" /> Manual</button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-12"><Loader2 className="w-6 h-6 animate-spin text-coral" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-white rounded-2xl border border-[#E3E7E2] p-4">
              <div className="flex items-center justify-between">
                <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-coral text-coral" : "text-[#E3E7E2]"}`} />)}</div>
                <button onClick={() => remove(r)} className="grid place-items-center w-8 h-8 rounded-lg text-muted-moss hover:text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4" /></button>
              </div>
              <p className="text-sm text-charcoal/80 mt-2 leading-relaxed">"{r.text}"</p>
              <p className="text-xs text-muted-moss mt-2 font-semibold">{r.author} · {r.location}</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl bg-cream border-[#E3E7E2]">
          <DialogTitle className="font-display text-xl font-bold text-charcoal">Nueva reseña</DialogTitle>
          <DialogDescription className="sr-only">Formulario de reseña</DialogDescription>
          <div className="space-y-3">
            <input value={form.author} onChange={set("author")} placeholder="Nombre del cliente" className="inp" />
            <textarea rows={3} value={form.text} onChange={set("text")} placeholder="Reseña" className="inp resize-none" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.rating} onChange={set("rating")} className="inp">{[5, 4, 3].map((n) => <option key={n} value={n}>{n} estrellas</option>)}</select>
              <input value={form.location} onChange={set("location")} placeholder="Ubicación" className="inp" />
            </div>
            <button onClick={save} disabled={saving} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-coral text-white font-bold hover:bg-coral-dark disabled:opacity-60">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Guardar
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
